import React from "react";

export default {
  messages: {
    "active-downloads": (
      <span>
        Information about creative assets and applications being downloaded.
        Updated metrics are available every 30 seconds.
        <ul>
          <li>
            <b>bytesReceived</b>: The number of bytes downloaded
          </li>
          <li>
            <b>contentLength</b>: The total size of the file, in bytes
          </li>
          <li>
            <b>elapsedMs</b>: The time passed since the download started, in
            milliseconds
          </li>
          <li>
            <b>progress</b>: The percentage of the file already downloaded
          </li>
          <li>
            <b>url</b>: Link to the file being downloaded
          </li>
        </ul>
      </span>
    ),
    "^cache::": (
      <span>
        Disk cache entries for an app. Each entry is stored with the remote
        resource url.
        <ul>
          <li>
            <b>path</b>: Local path of the cache entry
          </li>
          <li>
            <b>contentLength</b>: Content length in bytes
          </li>
          <li>
            <b>contentType</b>: Content type of the remote resource
          </li>
          <li>
            <b>expiration</b>: Cache expiration time in milliseconds
          </li>
        </ul>
      </span>
    ),
    "cache::snapshots": (
      <span>
        Information about the stored snapshots and its configuration.
        <ul>
          <li>
            <b>records</b>: list of creatives snapshotted recently
          </li>
          <li>
            <b>cacheExpiration</b>: time the creative will be removed from the
            device if not published
          </li>
          <li>
            <b>nextSnapshotTime</b>: time the creative is eligible to be
            snapshotted next
          </li>
          <li>
            <b>published</b>: whether most recent snapshot of the creative was
            published to Vistar’s server [true/false]
          </li>
          <li>
            <b>retrievedAt</b>: time the internal state was retrieved from the
            device
          </li>
          <li>
            <b>snapshotTime</b>: time of the most recent snapshot of the
            creative
          </li>
          <li>
            <b>settings</b>: configurable flags and fixed settings controlling
            the player’s snapshot mechanism
          </li>
        </ul>
      </span>
    ),
    "api::data": (
      <span>
        Internal state of Silo (deprecated) data listeners. This section
        contains information about the apps listening to Silo (deprecated)
        dataset changes.
        <ul>
          <li>
            <b>lastData</b>: Timestamp of the last data update
          </li>
          <li>
            <b>numData</b>: Number of times the player received an update since
            last restart
          </li>
          <li>
            <b>listeners</b>: List of apps listening to a dataset
          </li>
        </ul>
      </span>
    ),
    data_cache: (
      <span>
        Last good Silo (deprecated) data updates the player passed to
        applications. When you update Silo (deprecated) you should see this
        section updated as well.
      </span>
    ),
    flags: (
      <span>
        The flags passed to this player with the <i>flags</i> file. The flags
        you see here may not be visible with your operating system&apos;s task
        manager.
      </span>
    ),
    http_queue: (
      <span>
        Details on http queue behavior and resource consumption. The http queue
        is used to buffer and retry HTTP requests for traffic not directly
        related to on-screen playback such as proof-of-plays.
      </span>
    ),
    persisted_event_publisher: (
      <span>
        Details on event publisher behavior and resource consumption. The
        persisted event publisher is used to buffer and periodically publish
        player events.
      </span>
    ),
    currentSchedule: (
      <span>The current schedule is retrieved from the schedule endpoint.</span>
    ),
    currentLoop: (
      <span>
        The current loop shows the exact sequence of creatives the device is
        rotating through right now, including the currently playing slice from
        the schedule endpoint.
      </span>
    ),
    currentLogs: (
      <span>
        The current logs show the log files stored on this device right now,
        including their names and sizes.
      </span>
    ),
    "^player$": (
      <span>
        Internal state of the player.
        <ul>
          <li>
            <b>mimeTypes</b>: Mime types supported by this player
          </li>
          <li>
            <b>os</b>: Operating system details
          </li>
          <li>
            <b>processId</b>: The OS process id for the main player process.
            Killing this process will kill the player including all apps.
          </li>
          <li>
            <b>strategy</b>: The strategy running on this player
          </li>
          <li>
            <b>time</b>: The local player time
          </li>
          <li>
            <b>version</b>: The player version number
          </li>
          <li>
            <b>window</b>: Details of the player window
          </li>
        </ul>
      </span>
    ),
    "player-processes": (
      <span>
        List of all player processes running on this device. The Cortex player
        starts several processes during its normal operation. The <i>Browser</i>{" "}
        process is an internal process you can generally ignore. The{" "}
        <i>cortex-player</i> process is the main player process that handles
        player operations. On some operating systems you may see an additional{" "}
        <i>GPU Process</i> process which handles GPU related tasks. Aside from
        these processes, you will see a number of Cortex app processes depending
        on your strategy.
        <ul>
          <li>
            <b>chromePid</b>: Internal PID
          </li>
          <li>
            <b>memory</b>: Private set size of this process
          </li>
          <li>
            <b>osPid</b>: Operating system PID of this process
          </li>
          <li>
            <b>tasks</b>: A list of identifiers for this process
          </li>
        </ul>
      </span>
    ),
    registration: (
      <span>
        Player registration details.
        <ul>
          <li>
            <b>client.id</b>: Player id as seen on Fleet
          </li>
          <li>
            <b>client.code</b>: The registration code used to register this
            player
          </li>
        </ul>
      </span>
    ),
    "repository::html5": (
      <span>
        HTML5 app cache entries. These entries are similar to disk cache entries
        but we store them in a separate directory for internal reasons. If you
        have a dynamic campaign running on this player you should see an entry
        for your creative.
      </span>
    ),
    "repository::native": (
      <span>
        Cortex app repository. This section will contain all apps installed on
        this player.
        <ul>
          <li>
            <b>_params</b>: A list of parameters registered for this app. This
            list will be empty most of the time. The actual parameter list an
            app receives can be seen on the <i>player &gt; strategy</i> section.
          </li>
          <li>
            <b>_manifest</b>: Basic information from the <i>manifest.json</i>
            file in the app&apos;s package.
          </li>
          <li>
            <b>_index</b>: The path of the entry point of this app on local disk
          </li>
        </ul>
      </span>
    ),
    restarter: (
      <span>
        Details of auto restart schedule for this player if it has one.
        <ul>
          <li>
            <b>lastSchedule</b>: The last restart schedule the player received.
            The format is DayOfWeek-HourOfDay.
          </li>
          <li>
            <b>targetRestartTime</b>: Timestamp for the next restart.
          </li>
          <li>
            <b>lastUpdateScheduleCall</b>: Timestamp of the last schedule update
            call the player received
          </li>
        </ul>
      </span>
    ),
    "^storage$": (
      <span>
        The contents of the internal player storage. The player uses this
        section to store its settings. These settings survive player restarts.
        Most of the time you won&apos;t care about the contents of this file.
      </span>
    ),
    "system::cpu": (
      <span>
        System CPU details. Most interesting part is the
        <i>processors</i> section.
        <ul>
          <li>
            <b>user</b>: The cumulative time used by userspace programs
          </li>
          <li>
            <b>kernel</b>: The cumulative time used by kernel programs
          </li>
          <li>
            <b>idle</b>: The cumulative idle time
          </li>
          <li>
            <b>total</b>: user + kernel + idle
          </li>
        </ul>
      </span>
    ),
    "system::memory": (
      <span>
        System memory details.
        <ul>
          <li>
            <b>availableCapacityGB</b>: Available capacity in GBs
          </li>
          <li>
            <b>availableCapacity</b>: Available capacity in bytes
          </li>
          <li>
            <b>capacityGB</b>: Total capacity in GBs
          </li>
          <li>
            <b>capacity</b>: Total capacity in bytes
          </li>
        </ul>
      </span>
    ),
    "system::processes": (
      <span>
        Details on other processes reported by the device's operating system.
        <ul>
          <li>
            <b>currentLoad:</b> total CPU usage on the device
          </li>
          <li>
            <b>filteredProcessCount:</b> number of processes or applications
            using more than 1% of available memory or CPU
          </li>
          <li>
            <b>totalProcessCount:</b> total number of processes or applications
            running on the device
          </li>
          <li>
            <b>processes:</b> list of processes or applications using more than
            1% of available memory or CPU. On v6+, CortexPlayer (the Player
            Manager) and CortexPlayerNwjs are always listed if running,
            regardless of resource usage.
            <ul>
              <li>
                <b>name:</b> name of the process or application
              </li>
              <li>
                <b>cpu:</b> percentage of total CPU in use by the process (shown
                as a percentage, not a decimal)
              </li>
              <li>
                <b>mem:</b> percentage of total memory in use by the process
                (shown as a percentage, not a decimal)
              </li>
              <li>
                <b>started:</b> the date and time the process started, according
                to the device clock
              </li>
            </ul>
          </li>
        </ul>
      </span>
    ),
    "system::storage": (
      <span>
        System storage details for each attached storage unit.
        <ul>
          <li>
            <b>free</b>: Available capacity in bytes
          </li>
          <li>
            <b>total</b>: Total capacity in bytes
          </li>
        </ul>
      </span>
    ),
    "system::network": (
      <span>
        System network details.
        <ul>
          <li>
            <b>hostname</b>: The hostname of the device
          </li>
          <li>
            <b>interfaces</b>: List of network interfaces available on this
            device
          </li>
        </ul>
      </span>
    ),
    "system::temperature": (
      <span>
        Internal temperature of your device. Some or all of this data is
        unavailable to Cortex on some hardware and operating systems.
        <ul>
          <li>
            <b>cpu</b>: Temperature (degrees celsius) of your CPU
          </li>
          <li>
            <b>cpuDetail</b>: Temperature of each CPU core
          </li>
          <li>
            <b>disk</b>: Temperature (degrees celsius) of your disk
          </li>
          <li>
            <b>diskDetail</b>: Information about your disk, like its type
          </li>
        </ul>
      </span>
    ),
    window: (
      <span>
        Internal state of player <i>window</i>s. These entries represent the
        state of each application: Are they currently visible on screen? What
        portion of the screen are they covering?
        <ul>
          <li>
            <b>hasContentWindow</b>: <i>true</i> when an app successfully
            initializes
          </li>
          <li>
            <b>style.height</b>: The height of the app window
          </li>
          <li>
            <b>style.width</b>: The width of the app window
          </li>
          <li>
            <b>style.opacity</b>: The opacity of the app window
          </li>
          <li>
            <b>style.visibility</b>: The visibility of the app window
          </li>
        </ul>
      </span>
    ),
    watchdog: (
      <span>
        Details of the watchdog installation on this device.
        <ul>
          <li>
            <b>downloadPaused</b>: <i>true</i> when downloading watchdog is
            paused
          </li>
          <li>
            <b>running</b>: Watchdog process status
          </li>
          <li>
            <b>suspend</b>: <i>true</i> when watchdog process has been suspended
          </li>
          <li>
            <b>version</b>: Watchdog version number
          </li>
        </ul>
      </span>
    ),
  },
};
